import { cloneElement, isValidElement, useId, type ReactElement, type ReactNode } from 'react';
import { AlertCircle, Info } from 'lucide-react';
import { useTranslation } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { Label } from './label';
import { Tooltip } from './tooltip';

/**
 * Label + control + error, the unit every form is built from.
 *
 * The label is wired to the control here rather than at each call site. It used
 * to take an `htmlFor` that almost nothing passed, so no input in the
 * application had an id: clicking a label focused nothing, and a screen reader
 * read the patient registration form as twenty-two unnamed boxes — the
 * forced password-change screen as three unnamed password boxes in a row.
 *
 * The id is generated and handed to the single control this field wraps, along
 * with the hint or error as its description. A field whose child is a plain
 * element (a row of two controls in a `div`, say) is left alone: an id on a
 * `div` would point the label at something that cannot take focus.
 */
export function Field({
  label,
  error,
  hint,
  required,
  htmlFor,
  className,
  children,
  hintBelow,
}: {
  /** Omitted when a row of fields is already labelled by a shared header. */
  label?: string;
  error?: string;
  hint?: string;
  required?: boolean;
  /** Only needed when the control is rendered somewhere this cannot reach it. */
  htmlFor?: string;
  className?: string;
  children: ReactNode;
  hintBelow?: boolean;
}) {
  const common = useTranslation('common');
  const generated = useId();
  const controlId = htmlFor ?? generated;
  const messageId = error ? `${controlId}-error` : hint ? `${controlId}-hint` : undefined;

  type Wireable = { id?: string; 'aria-describedby'?: string; 'aria-invalid'?: boolean };
  const child = children as ReactElement<Wireable> | ReactNode;
  const wired =
    !htmlFor && isValidElement<Wireable>(child) && typeof child.type !== 'string' && !child.props.id
      ? cloneElement(child, {
          id: controlId,
          'aria-describedby': messageId ?? child.props['aria-describedby'],
          ...(error ? { 'aria-invalid': true } : {}),
        })
      : children;

  return (
    <div data-required-field={required || undefined} className={cn('group/field space-y-1.5', className)}>
      {label && (
        <span className="flex min-h-[1.125rem] items-center gap-1.5">
          <Label id={`${controlId}-label`} htmlFor={controlId} required={required}>
            {label}
          </Label>
          {hint && !error && !hintBelow && (
            <Tooltip content={hint} side="top" align="start">
              <button
                type="button"
                tabIndex={-1}
                aria-label={hint}
                className="grid h-4 w-4 shrink-0 place-items-center rounded-full text-muted-foreground/70 transition-colors hover:text-primary focus-ring"
              >
                <Info className="h-3.5 w-3.5" aria-hidden />
              </button>
            </Tooltip>
          )}
        </span>
      )}
      {wired}
      {required && !error && (
        <p data-missing-message className="hidden items-start gap-1 text-xs font-medium text-destructive group-data-[missing]/field:flex">
          <AlertCircle className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
          <span>{common.t('form.field_required')}</span>
        </p>
      )}
      {error ? (
        <p
          id={messageId}
          className="flex items-start gap-1 text-xs font-medium text-destructive animate-in fade-in-0 slide-in-from-top-1 duration-150"
        >
          <AlertCircle className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
          <span>{error}</span>
        </p>
      ) : hint ? (
        <p id={messageId} className={cn('text-xs text-muted-foreground', !hintBelow && 'sr-only')}>
          {hint}
        </p>
      ) : null}
    </div>
  );
}
